import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { concatMap, filter, map } from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { CanvasElement, getNamespaceId } from '@blaze-runtime/engine-domain';
import { WithBulkActions } from '@blaze-shared/elements-domain';
import { BulkActionType, getValueByPath } from '@blaze-shared/workflow-domain';

import { hasBulkAction, hasProps } from '../../canvas-elements/element-model/element-props-guards';
import { ElementDefinitionsService } from '../../services/element-definitions.service';
import { BulkActionNodesService } from '../../services/workflow-generation/public-api';
import { findActionConnection } from '../../utils/runtime/connection';
import { setElementProps } from '../elements/elements.actions';
import { getElementEntities } from '../elements/elements.selectors';
import { createElementWorkflow } from '../runtime/workflow.actions';

import { generateBulkAction, removeBulkAction } from './bulk-actions.actions';

@Injectable()
export class BulkActionsEffects {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);
  private readonly bulkActionNodesService = inject(BulkActionNodesService);
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);

  generateBulkAction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(generateBulkAction),
      concatLatestFrom(() => this.store.select(getElementEntities)),
      map(([action, entities]) => ({
        action,
        element: entities[action.elementId],
      })),
      filter((ctx): ctx is { action: typeof ctx.action; element: CanvasElement } => {
        const { element, action } = ctx;

        if (!element || !hasProps(element)) {
          return false;
        }

        const currentWorkflowId = getValueByPath<string>(element.props, action.workflowIdPath);
        return !currentWorkflowId;
      }),

      map(({ action, element }) => {
        return createElementWorkflow({
          elementId: element.id,
          inputSchemas: [],
          isLongRunningWf: true,
          nodes: this.bulkActionNodesService.getBulkActionNodes(action.actionType, element),
          ns: getNamespaceId(element.id),
          workflowIdPath: action.workflowIdPath,
        });
      }),
    );
  });

  removeBulkAction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(removeBulkAction),
      concatLatestFrom(() => [
        this.store.select(getElementEntities),
        this.store.select(runtimeSelectors.selectConnections),
      ]),
      map(([action, elements, connections]) => {
        const element = elements[action.elementId];
        if (!element) {
          return null;
        }

        const prop = getElementBulkAction(action.actionType);

        if (!prop || !hasBulkAction(element, prop)) {
          return null;
        }

        const bulkActionConfig = element.props.content[prop];
        const targetId = bulkActionConfig?.targetId;
        const targetType = bulkActionConfig?.targetType;

        if (targetId && (!targetType || targetType === 'Workflow')) {
          return {
            element,
            connections,
            prop,
            targetId,
          };
        }

        return null;
      }),
      filter((ctx): ctx is NonNullable<typeof ctx> => !!ctx),
      concatMap(({ element, connections, prop, targetId }) => {
        const propsBuilder = this.elementDefinitionsService.getPropsBuilder(
          element.type,
          element.props,
        );

        if (!propsBuilder) {
          throw new Error(`Missing props builder for ${element.type}`);
        }

        const props = propsBuilder.removeBulkAction(prop).build();
        const actions: Action[] = [setElementProps({ elementId: element.id, props })];

        const connection = findActionConnection(connections, element.id, targetId);

        if (connection) {
          actions.push(runtimeActions.connectionRemove({ id: connection.id }));
        }
        return actions;
      }),
    );
  });
}

function getElementBulkAction(
  actionType: BulkActionType,
): keyof Omit<WithBulkActions, 'bulkActions'> | undefined {
  switch (actionType) {
    case 'delete':
      return 'bulkDelete';
    case 'export':
      return 'bulkExport';
    case 'edit':
      return 'bulkEdit';
    default:
      return undefined;
  }
}
